import React, { Dispatch, SetStateAction, useCallback, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { IconButton } from "@atlaskit/button-next/new";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import Popup, { TriggerProps } from "@atlaskit/popup";
import { media, Text } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { getProductData, ProductKey } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";

import { JiraTemplateChoice } from "../../../hooks";
import { UserSegmentationTeamTypes } from "../../../models/user-segmentation";
import { messages } from "../messages";
import {
    productKeyAttributes,
    TemplateOptionMetadata
} from "../template-definitions";

interface CardProps {
    imageSrc: string;
    mobileImgSrc: string;
    title: string;
    subtext: string;
    templateType: JiraTemplateChoice;
    templateId: string;
    setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
    setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
    selectedCardTitle: string | undefined;
    setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
    teamType: UserSegmentationTeamTypes;
    templateMetadata?: TemplateOptionMetadata;
    setSelectedTemplateMetadata: Dispatch<
        SetStateAction<TemplateOptionMetadata | undefined>
    >;
    info?: {
        title: string;
        content: string;
    };
    productKey: ProductKey;
}

const cardBorderStyle = css({
    margin: "1px",
    padding: "0px",
    borderRadius: "var(--rounding, 8px)",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #C1C7D0",
    background: wacToken("color.neutral.light0"),
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    maxWidth: "463px",
    alignItems: "flex-start",
    cursor: "pointer",
    ["&:hover"]: {
        boxShadow:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)"
    },
    [media.above.lg]: {
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "none",
        minHeight: "333px",
        flex: 1,
        alignSelf: "flex-start"
    }
});

const unselectedButtonStyle = css({
    borderColor: token("color.background.accent.gray.subtlest")
});

const selectedButtonStyle = css({
    border: `2px solid ${token("color.link")}`,
    margin: 0
});

const cardTitleStyle = css({
    fontFamily: wacToken("font.charlie.display"),
    color: token("color.text"),
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "20px" /* 125% */,
    margin: "0 0 5px 0",
    [media.above.lg]: {
        fontSize: "16px",
        lineHeight: "24px"
    }
});

const titleContainerStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.050"),
    marginBlockEnd: token("space.075")
});

const poweredByContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    marginBlockStart: token("space.100"),
    marginBlockEnd: token("space.100")
});

const legalNoticeDesktopStyle = css({
    display: "none",
    [media.above.lg]: {
        display: "block"
    }
});

const legalNoticeMobileStyle = css({
    display: "flex",
    flexDirection: "row",
    [media.above.lg]: {
        display: "none"
    }
});

const poweredByTextStyle = css({
    fontSize: "12px",
    color: token("color.text.subtle"),
    fontWeight: 400,
    lineHeight: "16px",
    margin: 0,
    [media.above.lg]: {
        fontSize: "14px",
        lineHeight: "20px"
    }
});

const poweredByProductStyle = css({
    color: wacToken("color.primary.black")
});

const popupContentStyle = css({
    padding: token("space.100"),
    maxWidth: "280px"
});

const popupTitleStyle = css({
    fontFamily: token("font.body"),
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const popupTextStyle = css({
    fontFamily: token("font.body"),
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "16px",
    color: wacToken("color.primary.black")
});

const textContainerStyle = css({
    fontFamily: wacToken("font.charlie.text"),
    padding: token("space.200"),
    paddingBlockEnd: 0,
    display: "flex",
    flexDirection: "column",
    textAlign: "start",
    [media.above.lg]: {
        flexGrow: 1
    }
});

const contentWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const templateContentStyle = css({
    display: "flex"
});

const mobileImageSpacer = css({
    minWidth: "80px"
});

const legalNoticeWrapperStyle = css({
    padding: `0 ${token("space.200")} 0 ${token("space.200")}`,
    textAlign: "left"
});

const imgStyle = css({
    [media.above.lg]: {
        width: "100%",
        height: "100%"
    }
});

const mobileImgStyle = css({
    width: "80px",
    height: "100px",
    alignSelf: "center",
    [media.above.lg]: {
        display: "none"
    }
});

const desktopImageContainer = css({
    display: "none",
    [media.above.lg]: {
        display: "block",
        marginBlockEnd: token("space.100")
    }
});

const poweredByProductWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: token("space.100")
});

const legalNoticeContainerStyle = css({
    paddingBlockEnd: token("space.200")
});

interface PoweredBySectionProps {
    productKey: ProductKey;
}

const PoweredBySection: React.FC<PoweredBySectionProps> = ({ productKey }) => {
    const Icon = productKeyAttributes[productKey]?.icon;
    const { productName } = getProductData(productKey);

    return (
        <div css={poweredByContainerStyle}>
            <span css={poweredByTextStyle}>
                <FormattedMessage {...messages.poweredByText} />
            </span>
            <div css={poweredByProductWrapperStyle}>
                {Icon && (
                    <Icon
                        appearance="brand"
                        size="xsmall"
                        shouldUseNewLogoDesign
                        label=""
                    />
                )}
                {productName && (
                    <span css={[poweredByTextStyle, poweredByProductStyle]}>
                        {productName}
                    </span>
                )}
            </div>
        </div>
    );
};

interface LegalNoticeSectionProps {
    productKey: ProductKey;
    isVisible: boolean;
}

const LegalNoticeSection: React.FC<LegalNoticeSectionProps> = ({
    productKey,
    isVisible
}) => {
    const LegalNotice = productKeyAttributes[productKey]?.legalNotice;
    const { productName } = getProductData(productKey);

    return (
        <div css={legalNoticeContainerStyle}>
            {isVisible && LegalNotice && productName && (
                <>
                    <div
                        css={[legalNoticeDesktopStyle, legalNoticeWrapperStyle]}
                    >
                        <LegalNotice
                            productName={productName}
                            productKey={productKey}
                        />
                    </div>
                    <div css={legalNoticeMobileStyle}>
                        <div css={mobileImageSpacer} />
                        <div css={legalNoticeWrapperStyle}>
                            <LegalNotice
                                productName={productName}
                                productKey={productKey}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const InfoIcon = () => <InformationCircleIcon size="small" label="info" />;

export const CardJsmGrowth220: React.FC<CardProps> = ({
    imageSrc,
    mobileImgSrc,
    title,
    subtext,
    setSelectedTemplate,
    templateType,
    templateId,
    setSelectedTemplateId,
    setSelectedCardTitle,
    selectedCardTitle,
    teamType,
    templateMetadata,
    setSelectedTemplateMetadata,
    info,
    productKey
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [isInfoOpen, setIsInfoOpen] = useState(false);

    const handlePopupClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setIsInfoOpen(!isInfoOpen);
        },
        [isInfoOpen]
    );

    const popupTrigger = useCallback(
        (triggerProps: TriggerProps) => (
            <IconButton
                {...triggerProps}
                isSelected={isInfoOpen}
                appearance="subtle"
                icon={InfoIcon}
                label="Info"
                onClick={handlePopupClick}
                isTooltipDisabled
                spacing="compact"
            />
        ),
        [isInfoOpen, handlePopupClick]
    );

    const getContent = useCallback(
        () => (
            <div css={popupContentStyle}>
                <div css={popupTitleStyle}>{info?.title}</div>
                <div css={popupTextStyle}>{info?.content}</div>
            </div>
        ),
        [info]
    );

    const isSelected = selectedCardTitle === title;

    return (
        <button
            css={[
                cardBorderStyle,
                isSelected ? selectedButtonStyle : unselectedButtonStyle
            ]}
            onClick={() => {
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId:
                        TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceCard,
                    attributes: {
                        atlasTicket: "ATLAS-61649",
                        teamType,
                        templateType,
                        templateId,
                        templateName: title,
                        isJsmGrowth220Enabled: true
                    }
                }).fire();

                setSelectedTemplate(templateType);
                setSelectedTemplateId(templateId);
                setSelectedCardTitle(title);
                setSelectedTemplateMetadata(templateMetadata);
            }}
        >
            <div css={desktopImageContainer}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <img src={imageSrc} alt="" css={imgStyle} />
            </div>
            <div css={contentWrapperStyle}>
                <div css={templateContentStyle}>
                    {/* eslint-disable-next-line no-restricted-syntax */}
                    <img src={mobileImgSrc} alt="" css={mobileImgStyle} />
                    <div css={textContainerStyle}>
                        <div css={titleContainerStyle}>
                            <h3 css={cardTitleStyle}>{title}</h3>
                            {info && (
                                <Popup
                                    isOpen={isInfoOpen}
                                    onClose={() => setIsInfoOpen(false)}
                                    placement="bottom-start"
                                    zIndex={510}
                                    content={getContent}
                                    trigger={popupTrigger}
                                />
                            )}
                        </div>
                        <Text variant="body">{subtext}</Text>
                        <PoweredBySection productKey={productKey} />
                    </div>
                </div>
                <LegalNoticeSection
                    productKey={productKey}
                    isVisible={isSelected}
                />
            </div>
        </button>
    );
};
